import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate, Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeft,
  ExternalLink,
  Globe,
  ShieldCheck,
  FileText,
  FileUp,
  Scale,
  Pencil,
  Plus,
  Archive,
  AlertTriangle,
  Tag,
} from 'lucide-react'
import type {
  VendorDetailResponse,
  VendorReviewsListResponse,
  ArchiveVendorResponse,
  VendorReviewListItem,
} from '@common/types'
import { ReportTypeBadge } from '@frontend/components/ui/report-type-badge'
import { api } from '@frontend/lib/api'
import { usePermissions } from '@frontend/hooks/usePermissions'
import { formatDate, formatTime, isHttpUrl } from '@frontend/lib/utils'
import { AppLayout } from '@frontend/components/layout/AppLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@frontend/components/ui/card'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'
import { ReviewStatusBadge, ValidityBadge } from '@frontend/components/ui/status-badge'
import { VendorRiskBadge } from '@frontend/components/ui/vendor-badges'
import { MetadataItem } from '@frontend/components/ui/metadata-item'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  SortableTableHead,
} from '@frontend/components/ui/table'
import { useSort, type SortColumnDef } from '@frontend/lib/filters'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@frontend/components/ui/dialog'
import { VendorModal } from '@frontend/components/vendors/VendorModal'
import { RunVendorReviewModal } from '@frontend/components/vendors/RunVendorReviewModal'
import { GenerateDocumentsDialog } from '@frontend/components/documents/GenerateDocumentsDialog'
import { resolveComplianceStatus } from '@common/utils/validity'

async function fetchVendor(id: string): Promise<VendorDetailResponse> {
  return api.get(`vendors/${id}`).json<VendorDetailResponse>()
}

async function fetchVendorReviews(id: string): Promise<VendorReviewsListResponse> {
  return api.get(`vendors/${id}/reviews`).json<VendorReviewsListResponse>()
}

function UrlValue({ url, label }: { url: string | null; label: string }) {
  if (!url) return <span className="text-muted-foreground">Not provided</span>
  if (!isHttpUrl(url)) return <span className="text-muted-foreground">Invalid URL</span>
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
    >
      {label}
      <ExternalLink className="h-3 w-3" />
    </a>
  )
}

export function VendorDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const queryClient = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [archiveOpen, setArchiveOpen] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor', id],
    queryFn: () => fetchVendor(id!),
    enabled: !!id,
  })

  const {
    data: reviewsData,
    isLoading: reviewsLoading,
    error: reviewsError,
  } = useQuery({
    queryKey: ['vendor-reviews', id],
    queryFn: () => fetchVendorReviews(id!),
    enabled: !!id,
  })

  const archiveMutation = useMutation({
    mutationFn: () => api.post(`vendors/${id}/archive`).json<ArchiveVendorResponse>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', id] })
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
      setArchiveOpen(false)
      toast.success('Vendor archived')
    },
    onError: () => {
      toast.error('Failed to archive vendor')
    },
  })

  const vendor = data?.vendor
  const reviews = reviewsData?.reviews ?? []
  const isArchived = !!vendor?.archivedAt

  const reviewSortColumns = useMemo<SortColumnDef<VendorReviewListItem>[]>(
    () => [{ id: 'created', accessor: (r) => r.createdAt }],
    []
  )

  const {
    sortedData: sortedReviews,
    toggleSort: toggleReviewSort,
    isSorted: isReviewSorted,
  } = useSort(reviews, reviewSortColumns, { field: 'created', direction: 'desc' })

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      </AppLayout>
    )
  }

  if (error || !vendor) {
    return (
      <AppLayout>
        <div className="py-16 text-center">
          <p className="text-destructive">Vendor not found.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/vendors')}>
            Back to Vendors
          </Button>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Back link */}
        <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => navigate('/vendors')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Vendors
        </Button>

        {/* Page header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{vendor.name}</h1>
            {isArchived && (
              <p className="mt-1 text-sm text-muted-foreground">
                Archived on {formatDate(vendor.archivedAt!)}
                {vendor.archivedBy && ` by ${vendor.archivedBy}`}
              </p>
            )}
          </div>
          {!isArchived && (
            <div className="flex items-center gap-2">
              {can('vendors.edit') && (
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
              {can('vendors.archive') && (
                <Button variant="outline" size="sm" onClick={() => setArchiveOpen(true)}>
                  <Archive className="h-4 w-4 mr-1" />
                  Archive
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Vendor metadata */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Vendor Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <MetadataItem icon={Globe} label="Website" value={<UrlValue url={vendor.website} label="Visit" />} />
              <MetadataItem
                icon={Tag}
                label="Aliases"
                value={
                  vendor.aliases.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {vendor.aliases.map((alias) => (
                        <span key={alias} className="inline-flex items-center px-2 py-0.5 bg-muted rounded-md text-sm">
                          {alias}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">None</span>
                  )
                }
              />
              <MetadataItem
                icon={ShieldCheck}
                label="Trust Center"
                value={<UrlValue url={vendor.trustCenterUrl} label="View" />}
              />
              <MetadataItem
                icon={FileText}
                label="Privacy Policy"
                value={<UrlValue url={vendor.privacyPolicyUrl} label="View" />}
              />
              <MetadataItem
                icon={Scale}
                label="Terms of Service"
                value={<UrlValue url={vendor.tosUrl} label="View" />}
              />
              <MetadataItem icon={AlertTriangle} label="Risk Level" value={<VendorRiskBadge risk={vendor.risk} />} />
              <MetadataItem
                icon={FileText}
                label="Business Unit"
                value={vendor.businessUnit || <span className="text-muted-foreground">Not set</span>}
              />
              <MetadataItem
                icon={ShieldCheck}
                label="Stores PII"
                value={
                  vendor.storesPii === null ? (
                    <span className="text-muted-foreground">Unknown</span>
                  ) : vendor.storesPii ? (
                    'Yes'
                  ) : (
                    'No'
                  )
                }
              />
              <MetadataItem
                icon={ShieldCheck}
                label="Is Subprocessor"
                value={
                  vendor.isSubprocessor === null ? (
                    <span className="text-muted-foreground">Unknown</span>
                  ) : vendor.isSubprocessor ? (
                    'Yes'
                  ) : (
                    'No'
                  )
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Reviews section */}
        <Card className="shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Compliance Reviews</CardTitle>
              {!isArchived && can('reviews.create') && (
                <Button size="sm" onClick={() => setReviewOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Run Review
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {reviewsLoading ? (
              <div className="flex justify-center py-8">
                <Spinner size="md" />
              </div>
            ) : reviewsError ? (
              <div className="py-8 text-center">
                <p className="text-destructive">Failed to load reviews.</p>
              </div>
            ) : reviews.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-muted-foreground">No compliance reviews yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-[140px]">Report Type</TableHead>
                      <TableHead className="w-[120px]">Status</TableHead>
                      <TableHead className="w-[220px]">Validity</TableHead>
                      <TableHead className="w-[80px]">Docs</TableHead>
                      <SortableTableHead
                        className="w-[150px]"
                        sorted={isReviewSorted('created')}
                        onSort={() => toggleReviewSort('created')}
                      >
                        Created
                      </SortableTableHead>
                      <TableHead className="w-[80px] text-right">Source</TableHead>
                      <TableHead className="w-[100px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedReviews.map((review) => (
                      <ReviewRow
                        key={review.id}
                        review={review}
                        vendorName={vendor.name}
                        isLatestReview={review.isLatestReview ?? false}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Vendor Modal */}
      {vendor && !isArchived && <VendorModal mode="edit" vendor={vendor} open={editOpen} onOpenChange={setEditOpen} />}

      {/* Run Review Modal */}
      {vendor && !isArchived && (
        <RunVendorReviewModal
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          vendorId={vendor.id}
          vendorName={vendor.name}
        />
      )}

      {/* Archive Confirmation Dialog */}
      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Vendor</DialogTitle>
            <DialogDescription>
              Are you sure you want to archive <strong>{vendor.name}</strong>? This will hide the vendor from the main
              list. Existing reviews will not be affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveOpen(false)} disabled={archiveMutation.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => archiveMutation.mutate()} disabled={archiveMutation.isPending}>
              {archiveMutation.isPending ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Archiving...
                </>
              ) : (
                'Archive Vendor'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}

function ReviewRow({
  review,
  vendorName,
  isLatestReview,
}: {
  review: VendorReviewListItem
  vendorName: string
  isLatestReview: boolean
}) {
  const [generateOpen, setGenerateOpen] = useState(false)
  // Shared combiner: a valid bridge letter keeps an expired review uploadable.
  const compliance = resolveComplianceStatus({
    auditPeriodEnd: review.auditPeriodEnd,
    bridgeLetter: review.bridgeLetter,
    isLatestReview,
  })
  const canGenerate = review.status === 'complete' && compliance.isUploadable

  return (
    <>
      <TableRow className="group">
        <TableCell>
          <Link to={`/reviews/${review.id}`} className="hover:underline">
            <ReportTypeBadge reportType={review.reportType} />
          </Link>
        </TableCell>
        <TableCell>
          <ReviewStatusBadge status={review.status} />
        </TableCell>
        <TableCell>
          <ValidityBadge
            auditPeriodEnd={review.auditPeriodEnd}
            bridgeLetter={review.bridgeLetter}
            isLatestReview={isLatestReview}
          />
        </TableCell>
        <TableCell>{review.documentCount}</TableCell>
        <TableCell>
          <div className="text-sm">
            <div>{formatDate(review.createdAt)}</div>
            <div className="text-muted-foreground">{formatTime(review.createdAt)}</div>
          </div>
        </TableCell>
        <TableCell className="text-right">
          {review.sourceUrl ? (
            <a
              href={review.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-primary"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell>
          {canGenerate && (
            <Button variant="outline" size="sm" onClick={() => setGenerateOpen(true)}>
              <FileUp className="h-4 w-4 mr-1" />
              Generate
            </Button>
          )}
        </TableCell>
      </TableRow>
      {canGenerate && (
        <GenerateDocumentsDialog
          reviewId={review.id}
          vendorName={vendorName}
          open={generateOpen}
          onOpenChange={setGenerateOpen}
        />
      )}
    </>
  )
}
